<template>
  <page :name="name" :title="title" :state="[ticketState, messagesState, productState]" sticky>
    <template #breadcrumbs>
      <pf-breadcrumb-item :to="{ name: 'tickets' }">{{ $t('Tickets') }}</pf-breadcrumb-item>
    </template>

    <template #toolbar-post v-if="ticket.id || idTicket === 'new'">
      <template v-if="ticket.id">
        <pf-label v-if="ticket.urgente" color="red" class="pf-v6-u-mr-sm">
          <template #icon>
            <triangle-exclamation-icon />
          </template>
          {{ $t('Urgente') }}
        </pf-label>
        <pf-label v-if="ticket.prodotto" color="grey">
          {{ $t('Prodotto: {0}', [ticket.prodotto]) }}
        </pf-label>
        <pf-label v-if="ticket.department" color="grey" class="pf-v6-u-mr-sm">
          {{ $t('Tipologia: {0}', [ticket.department]) }}
        </pf-label>
        <pf-label :color="ticket.stato == 'aperto' ? 'green' : 'grey'">
          {{ $t('Stato: {0}', [ticket.stato == 'aperto' ? $t('Aperto') : $t('Chiuso')]) }}
        </pf-label>
      </template>
      <pf-label v-else color="grey">
        {{ $t('Nuovo') }}
      </pf-label>
    </template>

    <pf-page-section v-if="!ticket.id && idTicket !== 'new'">
      <pf-alert :title="$t('Ticket non trovato')" variant="danger">
        <pf-button variant="link" @click="idTicket = 'new'">
          {{ $t('Apri un nuovo ticket') }}
        </pf-button>
      </pf-alert>
    </pf-page-section>

    <template v-else>
      <pf-page-section v-if="ticket.id">
        <pf-empty-state v-if="!messages || !messages.length">
          <pf-empty-state-header :title="$t('Nessun messaggio presente')" heading-level="h4">
            <template #icon>
              <magnifying-glass-icon />
            </template>
          </pf-empty-state-header>
        </pf-empty-state>

        <pf-progress-stepper v-else vertical>
          <pf-progress-step v-for="message of messages" :key="message.id">
            <template #icon>
              <img :src="avatar(message)" />
            </template>

            <pf-panel :class="['pf-m-ticket-message', { 'pf-m-ticket-operator-message': message.operatore }]">
              <pf-panel-header>
                <pf-flex>
                  <pf-flex-item component="strong" v-if="message.operatore">
                    {{ message.operatore }}
                  </pf-flex-item>
                  <pf-flex-item component="strong" v-else-if="message.utente">
                    {{ message.utente }}
                  </pf-flex-item>
                  <pf-flex-item component="strong" v-else>{{ $t('Hai scritto:') }}</pf-flex-item>
                  <pf-flex-item>
                    <pf-content component="small">{{ $datetime(message.data) }}</pf-content>
                  </pf-flex-item>
                </pf-flex>
              </pf-panel-header>
              <pf-panel-main>
                <pf-panel-main-body>
                  <iframe-autoheight
                    v-if="message.operatore"
                    referrerpolicy="no-referrer"
                    sandbox="allow-same-origin allow-popups"
                    :srcdoc="messageHtml(message.messaggio)"
                  />
                  <template v-else>{{ message.messaggio }}</template>
                </pf-panel-main-body>
              </pf-panel-main>
            </pf-panel>

            <div v-if="message.allegati.length > 0 || message.id_offerta_allegata || message.id_fattura_allegata" class="pf-v6-u-mt-sm">
              {{ $t('Allegati') }}{{ ': ' }}
              <template v-if="message.allegati.length > 0">
                <pf-label v-for="file in message.allegati" :key="file.id" :href="`/rest/ticket_attachment/${file.id}`">
                  <template #icon>
                    <paperclip-icon />
                  </template>
                  {{ file.filename }}
                </pf-label>
              </template>
              <pf-label
                v-if="message.id_offerta_allegata"
                :key="message.id_offerta_allegata"
                :href="`/rest/offer-pdf/${message.id_offerta_allegata}`"
                color="orange"
              >
                <template #icon>
                  <file-invoice-dollar-icon />
                </template>
                {{ $t('Offerta nr {0}', [message.id_offerta_allegata]) }}
              </pf-label>
              <pf-label
                v-if="message.id_fattura_allegata"
                :key="message.id_fattura_allegata"
                :href="`/rest/invoice-pdf/${message.id_fattura_allegata}`"
                color="green"
              >
                <template #icon>
                  <file-lines-icon />
                </template>
                {{ $t('Fattura {0}', [`${message.nr_fattura}${message.serie_fattura} / ${message.anno_fattura}`]) }}
              </pf-label>
            </div>
          </pf-progress-step>
        </pf-progress-stepper>
      </pf-page-section>

      <pf-divider v-if="ticket.id" />

      <pf-page-section v-if="ticket.stato == 'aperto' || !ticket.id">
        <pf-panel variant="bordered">
          <pf-panel-main>
            <pf-panel-main-body>
              <x-form
                ref="form"
                encoding="json"
                :action="'/rest/tickets' + (ticket.id ? `/${ticket.id}/messages` : '')"
                :additional-data="additionalData"
                :submit-text="confirmMessage"
                xhr
                @submit="send"
                style="max-width: 80ch"
              >
                <template v-if="!ticket.id">
                  <pf-flex flex-wrap-md="nowrap">
                    <pf-flex-item>
                      <form-group :name="$t('Tipologia')" required>
                        <pf-form-select
                          name="department"
                          :model-value="department"
                          @update:model-value="department = $event as 'tecnica' | 'amministrativa' | 'commerciale'"
                        >
                          <pf-form-select-option value="tecnica">
                            {{ $t('Tecnica') }}
                          </pf-form-select-option>
                          <pf-form-select-option value="amministrativa">
                            {{ $t('Amministrativa') }}
                          </pf-form-select-option>
                          <pf-form-select-option value="commerciale">
                            {{ $t('Commerciale') }}
                          </pf-form-select-option>
                        </pf-form-select>
                      </form-group>
                    </pf-flex-item>

                    <pf-flex-item style="max-width: 300px">
                      <form-group v-if="Object.keys(productsByDomain).length > 0" :name="$t('Prodotto')" :required="department === 'tecnica'">
                        <template #field-help>
                          {{ $t('Seleziona il prodotto per cui vuoi richiedere assistenza') }}
                        </template>
                        <pf-form-select name="id_product" :required="department === 'tecnica'">
                          <pf-form-select-option value="new">
                            {{ $t('Nuova attivazione') }}
                          </pf-form-select-option>
                          <optgroup v-for="([domain, items], index) in Object.entries(productsByDomain)" :key="index" :label="domain">
                            <pf-form-select-option v-for="product in items" :key="product.id" :value="product.id">
                              {{ domain }} - {{ product.nome }}
                            </pf-form-select-option>
                          </optgroup>
                        </pf-form-select>
                      </form-group>
                    </pf-flex-item>
                  </pf-flex>

                  <form-group :name="$t('Titolo')" required style="max-width: 80ch">
                    <x-input id="ticketTitle" name="titolo" type="text" :placeholder="$t('Titolo')" required />
                  </form-group>
                </template>

                <form-group
                  v-if="ticket.id && (ticket.to.length !== 1 || ticket.to[0]?.email !== login.email)"
                  :name="$t('Proprietario')"
                  :field-help="
                    $t(
                      'Il proprietario di un ticket può rispondere, riceve le notifiche delle nostre risposte, e può aggiungere e togliere email in copia alle notifiche',
                    )
                  "
                >
                  <pf-label v-if="ticket.id && ticket.to.length" v-for="to in ticket.to" :key="to.email" :color="to.email === login.email ? 'green' : 'grey'">
                    {{ to.email === login.email ? $t('Tu') : to.email }}
                  </pf-label>

                  <pf-label v-else-if="ticket.id">{{ $t('Tutti gli utenti con notifiche tecniche') }}</pf-label>
                  <pf-label v-else color="green">{{ $t('Tu') }}</pf-label>
                </form-group>

                <form-group
                  v-if="access === 'owner'"
                  :name="$t('Email in copia')"
                  :field-help="$t('Gli indirizzi email in copia ricevereanno le notifiche delle nostre risposte via mail.')"
                >
                  <pf-flex direction="column" gap="md">
                    <pf-input-group v-for="(contact, index) in cc">
                      <pf-input-group-item fill>
                        <x-input
                          type="email"
                          :name="contact.email.trim() === '' ? '' : 'cc[]'"
                          v-model.lazy="contact.email"
                          :placeholder="$t('Email in CC')"
                          :ouia-id="`ticket-cc-input-${index}`"
                        />
                      </pf-input-group-item>
                      <pf-input-group-item>
                        <pf-button
                          :disabled="contact.email.trim() === ''"
                          @click="cc.splice(index, 1)"
                          inline
                          variant="control"
                          :title="$t('Rimuovi casella')"
                          :ouia-id="`ticket-cc-remove-${index}`"
                        >
                          <template #icon><trash-icon /></template>
                        </pf-button>
                      </pf-input-group-item>
                    </pf-input-group>
                  </pf-flex>

                  <!-- Input per mandare contatti con array vuoto -->
                  <input type="hidden" v-if="cc.length === 1 && cc[0] && cc[0].email === ''" name="cc[]" />
                </form-group>

                <form-group v-else-if="ticket.cc.length" :name="$t('Email in copia')">
                  <template v-for="cc in ticket.cc">
                    <pf-label v-if="ticket.id && cc.email === login.email" @close="unfollowTicket(ticket.id, ticketState.update)" color="green">
                      {{ $t('Tu') }}
                    </pf-label>
                    <pf-label v-else>{{ cc.email }}</pf-label>
                  </template>
                </form-group>

                <form-group :name="$t('Messaggio')" required>
                  <template #helper-text v-if="!ticket.id">
                    {{
                      $t(
                        'Specificare eventuale dominio di riferimento, IP del server dedicato o indirizzo del server cloud ed in generale un riferimento al servizio sul quale si richiede assistenza. Se possibile indicare come è possibile riprodurre il problema.',
                      )
                    }}
                    <br />
                    <strong>
                      {{
                        $t(
                          'Si prega di non inserire dati sensibili come password, token di accesso o chiavi. Se stai effettuando un trasferimento di servizi utilizza il form nella relativa pagina',
                        )
                      }}
                    </strong>
                  </template>
                  <pf-textarea id="messageText" name="messaggio" :placeholder="$t('Testo del messaggio')" rows="10" required />
                </form-group>

                <template v-if="areWeClosed">
                  <pf-alert
                    inline
                    v-if="!ticket.id && !login.is_premium"
                    variant="warning"
                    :title="$t('Al ticket verrà data risposta in orario di ufficio. In caso di urgenze selezionare la checkbox \'Urgente\'')"
                  />
                  <pf-alert
                    inline
                    v-else-if="!ticket.urgente && !login.is_premium"
                    variant="warning"
                    :title="$t('Al messaggio verrà data risposta in orario di ufficio. In caso di urgenze aprire un nuovo ticket di tipo \'Urgente\'')"
                  >
                    <router-link :to="{ name: 'ticket', params: { id: 'new' } }">
                      {{ $t('Apri un nuovo ticket') }}
                    </router-link>
                  </pf-alert>
                </template>

                <form-group v-if="!ticket.id && !login.is_premium" :name="$t('Urgente')" no-label>
                  <pf-checkbox name="urgente" value="1">
                    <template #label>{{ $t('Urgente') }}</template>
                    <template #description>
                      {{ $t('Il ticket verrà fatturato al costo di {0}', [formatCurrency(urgentTicketCost, login.currency)]) }}
                    </template>
                  </pf-checkbox>
                </form-group>

                <div v-if="attachments.length">
                  <label>{{ $t('Allegati') }}: </label>
                  <pf-label v-for="(file, index) in attachments" :key="index" @close="attachments.splice(index, 1)">
                    <template #icon>
                      <paperclip-icon />
                    </template>
                    {{ file.name }} ({{ $size(file.size) }})
                  </pf-label>
                </div>

                <template #submit> <paper-plane-icon /> {{ $t('Invia') }} </template>

                <template #actions>
                  <pf-button component="label" variant="control" style="cursor: pointer">
                    <template #icon>
                      <paperclip-icon />
                    </template>
                    {{ $t('Aggiungi allegati') }}
                    <input type="file" multiple style="display: none" @change="refreshAttachments" />
                  </pf-button>
                </template>
              </x-form>
            </pf-panel-main-body>
          </pf-panel-main>
        </pf-panel>
      </pf-page-section>
    </template>
  </page>
</template>

<style lang="scss" scoped>
.pf-v6-c-progress-stepper {
  --pf-v6-c-progress-stepper__step-main--MarginTop: 0;
  --pf-v6-c-progress-stepper__step-icon--Width: 3rem;
  --pf-v6-c-progress-stepper__step-icon--Height: 3rem;
  --pf-v6-c-progress-stepper__step-icon--ZIndex: 0;
}

.pf-v6-c-progress-stepper img {
  border-radius: 50%;
}

.pf-v6-c-panel.pf-m-ticket-message {
  iframe {
    width: 100%;
  }

  .pf-v6-c-panel__header {
    padding-top: 0;
    padding-bottom: 0;
    line-height: var(--pf-v6-c-progress-stepper__step-icon--Height);
  }

  .pf-v6-c-panel__main {
    white-space: pre-wrap;
    max-width: 80ch;
    overflow: auto;
    border-radius: 10px;
    background-color: var(--pf-v6-c-page--BackgroundColor);
  }

  &.pf-m-ticket-operator-message {
    white-space: initial;

    .pf-v6-c-panel__header strong {
      color: var(--pf-t--global--text--color--brand--default);
    }
  }
}
</style>

<script lang="ts" setup>
import md5 from 'nano-md5';

import IframeAutoheight from '@common/IframeAutoheight.vue';
import { asyncComputed, asyncComputedList } from '@common/asyncComputed';
import { unfollowTicket } from '@/components/ticket-list.vue';
import { formatCurrency } from '@common/utils';
import { useAppStore } from '@/store/app';
import { useLoginStore } from '@/store/login';
import {
  type PHPDateTime,
  type Ticket,
  UrgentTicketCostResource,
  AreWeClosedResource,
  type TicketMessage,
  type TicketContact,
  TicketResource,
  type ProductOrder,
  ProductOrdersResource,
} from '@/resources';
import { ref, type Ref, computed, watch } from 'vue';
import { $t as _t } from '@/i18n';
import { pop } from '@common/utils';
import type { Optional } from 'utility-types';
import { useRoute, useRouter } from 'vue-router';
import { useAlertStore } from '@/store/alerts';
import TriangleExclamationIcon from '@vue-patternfly/icons/triangle-exclamation-icon';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';
import FileLinesIcon from '@vue-patternfly/icons/file-lines-icon';
import FileInvoiceDollarIcon from '@vue-patternfly/icons/file-invoice-dollar-icon';
import TrashIcon from '@vue-patternfly/icons/trash-icon';
import PaperclipIcon from '@vue-patternfly/icons/paperclip-icon';
import PaperPlaneIcon from '@vue-patternfly/icons/paper-plane-icon';
import { watchDebounced } from '@vueuse/core';

const defaultTicket: Optional<Ticket, 'id' | 'data'> = {
  id: undefined,
  titolo: '',
  urgente: false,
  data: undefined,
  stato: 'aperto',
  nr_messaggi: 0,
  prodotto: null,
  department: null,
  cc: [],
  to: [],
};

const app = useAppStore();
const login = useLoginStore();
const route = useRoute();
const router = useRouter();
const idTicket: Ref<string | number | undefined> = ref(pop(route.params.id));

watch(
  () => route.params.id,
  () => {
    idTicket.value = pop(route.params.id);
  },
);

const { state: productState, result: products } = asyncComputedList(() => new ProductOrdersResource().get());

const productsByDomain = computed(() => {
  const groups: Record<string, ProductOrder[]> = {};
  for (const item of products.value) {
    const domain = item.dominio ?? 'Senza dominio';
    if (!groups[domain]) {
      groups[domain] = [];
    }
    groups[domain].push(item);
  }
  return groups;
});

const { result: ticket, state: ticketState } = asyncComputed(() => {
  if (idTicket.value !== 'new' && idTicket.value) {
    return new TicketResource().get(idTicket.value);
  }
  return defaultTicket;
}, defaultTicket);

function ticketAccess(to: TicketContact[], cc: TicketContact[], email: string): 'owner' | 'follower' | null {
  if ((to.length === 0 && login.email_subscriptions.includes('technical')) || to.some(contact => contact.email === email)) {
    return 'owner';
  }
  if (cc.some(contact => contact.email === email)) {
    return 'follower';
  }
  return null;
}

const access = computed(() => ticketAccess(ticket.value.to, ticket.value.cc, login.email));

const { result: messages, state: messagesState } = asyncComputedList(() => {
  if (idTicket.value) {
    return new TicketResource().messages(idTicket.value).get();
  }
  return [];
});

watch(
  () => ticket?.value.cc,
  () => {
    if (access.value === 'owner') {
      cc.value = ticket.value.cc ?? [{ email: '' }];
    }
  },
);

const { result: urgentTicketCost } = asyncComputed(() => new UrgentTicketCostResource().get(1));
const { result: areWeClosed } = asyncComputed(() => new AreWeClosedResource().get(1));

const attachments: Ref<File[]> = ref([]);
const urgente = ref(false);
const department: Ref<'tecnica' | 'amministrativa' | 'commerciale' | null> = ref(null);
const cc: Ref<TicketContact[]> = ref([]);

const name = computed(() => (ticket.value.id ? _t('Ticket: #{0}', [ticket.value.id]) : _t('Nuovo ticket')));
const title = computed(() =>
  ticket.value.id
    ? _t('Ticket nr. {0}: {1}', [ticket.value.id, ticket.value.titolo])
    : _t('Nuovo ticket') + (ticket.value.urgente ? ` [${_t('Urgente')}]` : ''),
);

const confirmMessage = computed(() => {
  return urgentTicketCost.value > 0 && urgente.value && !login.is_premium
    ? _t('Il ticket è stato impostato come urgente e verrà quindi fatturato al costo di {0}. Vuoi procedere?', [
        formatCurrency(urgentTicketCost.value, login.currency),
      ])
    : undefined;
});

const additionalData = computed(() => {
  return { attachments: attachments.value };
});

function send(_e: Event | undefined, responseData: unknown) {
  const alertStore = useAlertStore();
  attachments.value = [];
  if (ticket.value.id) {
    alertStore.add({ title: _t('Risposta inviata'), type: 'success', toast: true });
    messagesState.update();
    ticketState.update();
  } else {
    const newTicket = responseData as {
      id: number;
      data: PHPDateTime;
      messaggio: string;
      stato: 'aperto' | 'chiuso';
      titolo: string;
      attachments: any[];
      extra_time: boolean | null;
      urgente: boolean | null;
      to: [];
      cc: [];
    };
    idTicket.value = newTicket.id;
    alertStore.add({
      title:
        !newTicket.urgente && !login.is_premium && newTicket.extra_time
          ? _t('Ticket inviato. Riceverai risposta in orario di ufficio. Grazie!')
          : _t('Ticket inviato. Lo staff ti risponderà al più presto. Grazie!'),
      type: 'success',
      toast: true,
    });
    router.push({ name: 'ticket', params: { id: newTicket.id } });
  }
}

function messageHtml(messaggio: string) {
  const appStylesheet = document.querySelector<HTMLLinkElement>('link[rel=stylesheet]')?.outerHTML ?? '';

  return `<html class="${app.darkTheme ? 'pf-v6-theme-dark' : ''}"><head>${appStylesheet}<style>
html, body {
  padding: 0;
  margin: 0;
}

body {
  font-size: 9pt;
  background-color: transparent;
}

body > :first-child {
  margin-top: 0;
}

body > :last-child {
  margin-bottom: 0;
}

a {
  color: #80cfff;
  text-decoration: none;
}

blockquote {
  margin: 4pt 0 4pt 0;
  padding: 0 0 0 1em;
  border-left: 2px solid;
  unicode-bidi: -webkit-plaintext
}

blockquote,
blockquote blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote blockquote {
  border-color: #82ff95;
}

blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote blockquote blockquote {
  border-color: #69ce78;
}

blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote,
blockquote blockquote blockquote blockquote blockquote blockquote blockquote blockquote blockquote {
  border-color: #4b9456;
}

::-webkit-scrollbar {
  transition: width .2s linear;
  width: 8px;
  height: 8px
}

::-webkit-scrollbar-thumb {
  transition: background-color .2s linear;
  background-color: #aaa;
  background-color: #aaaaaa80;
  border-radius: 4px
}

::-webkit-scrollbar-thumb:hover {
  background-color: #999;
  background-color: #999999b3
}

::-webkit-scrollbar-track {
  background-color: transparent
}
</style></head>
<body class="pf-v6-c-content">${messaggio}</body></html>`;
}

function avatar(message: TicketMessage) {
  return `https://www.gravatar.com/avatar/${md5(message.utente ?? '')}?s=45&d=retro`;
}

function refreshAttachments(e: Event) {
  if (!(e.target instanceof HTMLInputElement) || !e.target.files) {
    return;
  }

  for (const file of e.target.files) {
    attachments.value.push(file);
  }
  try {
    e.target.value = '';
  } catch {
    // ignore
  }
}

watchDebounced(
  cc,
  () => {
    if (!cc.value.find(contact => contact.email.trim() === '')) {
      cc.value.push({ email: '' });
    }
  },
  { deep: true, debounce: 500, immediate: true },
);
</script>
